import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import {
    Video,
    Music,
    Plus,
    Search,
    Filter,
    ExternalLink,
    Trash2,
    Edit,
    Star,
    Clock,
    Users,
    Loader2,
    X,
    Youtube,
    Heart,
    Share2,
    MoreVertical,
    Tag,
    ChevronDown,
} from 'lucide-react';

interface CuratedContent {
    id: string;
    type: 'youtube' | 'spotify' | 'article' | 'podcast';
    title: string;
    description: string | null;
    url: string;
    thumbnail_url: string | null;
    duration_seconds: number | null;
    category: string;
    tags: string[];
    is_featured: boolean;
    view_count: number;
    created_at: string;
}

const CATEGORIES = [
    'Treino', 'Nutrição', 'Motivação', 'Técnica', 'Mobilidade',
    'Recuperação', 'Lifestyle', 'Receitas', 'Suplementação'
];

const CONTENT_TYPES = [
    { value: 'youtube', label: 'YouTube', icon: Youtube, color: 'text-red-500' },
    { value: 'spotify', label: 'Spotify', icon: Music, color: 'text-green-500' },
    { value: 'article', label: 'Artigo', icon: Video, color: 'text-blue-500' },
    { value: 'podcast', label: 'Podcast', icon: Music, color: 'text-purple-500' },
];

export function ContentPage() {
    const { tenant } = useAuth();
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
    const [typeFilter, setTypeFilter] = useState<string | null>(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        type: 'youtube' as 'youtube' | 'spotify' | 'article' | 'podcast',
        title: '',
        description: '',
        url: '',
        thumbnail_url: '',
        category: '',
        tags: [] as string[],
        is_featured: false,
    });

    // Fetch content
    const { data: content, isLoading } = useQuery({
        queryKey: ['curated-content', tenant?.id, categoryFilter, typeFilter],
        queryFn: async () => {
            if (!tenant?.id) return [];

            let query = supabase
                .from('curated_content')
                .select('*')
                .eq('tenant_id', tenant.id)
                .order('created_at', { ascending: false });

            if (categoryFilter) {
                query = query.eq('category', categoryFilter);
            }
            if (typeFilter) {
                query = query.eq('type', typeFilter);
            }

            const { data, error } = await query;
            if (error) throw error;
            return data as CuratedContent[];
        },
        enabled: !!tenant?.id,
    });

    // Add content mutation
    const addMutation = useMutation({
        mutationFn: async (data: typeof formData) => {
            // Extract YouTube thumbnail if URL is provided
            let thumbnailUrl = data.thumbnail_url;
            if (data.type === 'youtube' && data.url && !thumbnailUrl) {
                const videoId = extractYouTubeId(data.url);
                if (videoId) {
                    thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                }
            }

            const { error } = await supabase.from('curated_content').insert({
                tenant_id: tenant?.id,
                type: data.type,
                title: data.title,
                description: data.description || null,
                url: data.url,
                thumbnail_url: thumbnailUrl || null,
                category: data.category,
                tags: data.tags,
                is_featured: data.is_featured,
            });

            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['curated-content'] });
            closeModal();
        },
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            const { error } = await supabase
                .from('curated_content')
                .delete()
                .eq('id', id);

            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['curated-content'] });
        },
    });

    // Toggle featured
    const toggleFeaturedMutation = useMutation({
        mutationFn: async ({ id, featured }: { id: string; featured: boolean }) => {
            const { error } = await supabase
                .from('curated_content')
                .update({ is_featured: featured })
                .eq('id', id);

            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['curated-content'] });
        },
    });

    const closeModal = () => {
        setShowAddModal(false);
        setFormData({
            type: 'youtube',
            title: '',
            description: '',
            url: '',
            thumbnail_url: '',
            category: '',
            tags: [],
            is_featured: false,
        });
    };

    const extractYouTubeId = (url: string): string | null => {
        const match = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
        return match ? match[1] : null;
    };

    const formatDuration = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Filter content
    const filteredContent = content?.filter((c) =>
        c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.tags?.some((t) => t.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const featuredContent = filteredContent?.filter((c) => c.is_featured) || [];
    const regularContent = filteredContent?.filter((c) => !c.is_featured) || [];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="section-title flex items-center gap-2">
                        <Video className="w-6 h-6" />
                        Conteúdo Curado
                    </h1>
                    <p className="section-subtitle">
                        Compartilhe vídeos, músicas e artigos com seus alunos
                    </p>
                </div>
                <button onClick={() => setShowAddModal(true)} className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Conteúdo
                </button>
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Buscar conteúdo..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="input pl-10"
                    />
                </div>
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`btn-secondary ${showFilters ? 'bg-primary-50 text-primary-700' : ''}`}
                >
                    <Filter className="w-4 h-4 mr-2" />
                    Filtros
                    <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </button>
            </div>

            {/* Filter chips */}
            {showFilters && (
                <div className="card space-y-4">
                    <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Tipo</p>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setTypeFilter(null)}
                                className={`badge ${!typeFilter ? 'badge-primary' : 'badge-secondary'}`}
                            >
                                Todos
                            </button>
                            {CONTENT_TYPES.map((type) => (
                                <button
                                    key={type.value}
                                    onClick={() => setTypeFilter(type.value)}
                                    className={`badge ${typeFilter === type.value ? 'badge-primary' : 'badge-secondary'}`}
                                >
                                    {type.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Categoria</p>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setCategoryFilter(null)}
                                className={`badge ${!categoryFilter ? 'badge-primary' : 'badge-secondary'}`}
                            >
                                Todas
                            </button>
                            {CATEGORIES.map((cat) => (
                                <button
                                    key={cat}
                                    onClick={() => setCategoryFilter(cat)}
                                    className={`badge ${categoryFilter === cat ? 'badge-primary' : 'badge-secondary'}`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Featured Content */}
            {featuredContent.length > 0 && (
                <div className="space-y-4">
                    <h2 className="font-semibold flex items-center gap-2">
                        <Star className="w-5 h-5 text-yellow-500" />
                        Destaques
                    </h2>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {featuredContent.map((item) => (
                            <ContentCard
                                key={item.id}
                                content={item}
                                onDelete={() => deleteMutation.mutate(item.id)}
                                onToggleFeatured={() =>
                                    toggleFeaturedMutation.mutate({
                                        id: item.id,
                                        featured: !item.is_featured,
                                    })
                                }
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Regular Content */}
            <div className="space-y-4">
                <h2 className="font-semibold">
                    Todos os Conteúdos ({regularContent.length})
                </h2>
                {regularContent.length > 0 ? (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {regularContent.map((item) => (
                            <ContentCard
                                key={item.id}
                                content={item}
                                onDelete={() => deleteMutation.mutate(item.id)}
                                onToggleFeatured={() =>
                                    toggleFeaturedMutation.mutate({
                                        id: item.id,
                                        featured: !item.is_featured,
                                    })
                                }
                            />
                        ))}
                    </div>
                ) : (
                    <div className="card text-center py-12">
                        <Video className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                        <h3 className="font-medium text-gray-900 mb-2">Nenhum conteúdo ainda</h3>
                        <p className="text-gray-500 mb-4">
                            Adicione vídeos, músicas e artigos para seus alunos
                        </p>
                        <button onClick={() => setShowAddModal(true)} className="btn-primary">
                            <Plus className="w-4 h-4 mr-2" />
                            Adicionar Primeiro Conteúdo
                        </button>
                    </div>
                )}
            </div>

            {/* Add Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 my-8">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold">Adicionar Conteúdo</h2>
                            <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                addMutation.mutate(formData);
                            }}
                            className="space-y-4"
                        >
                            {/* Type */}
                            <div>
                                <label className="label">Tipo de Conteúdo</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {CONTENT_TYPES.map((type) => (
                                        <button
                                            key={type.value}
                                            type="button"
                                            onClick={() =>
                                                setFormData({ ...formData, type: type.value as typeof formData.type })
                                            }
                                            className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${formData.type === type.value
                                                ? 'border-primary-500 bg-primary-50'
                                                : 'border-gray-200 hover:border-gray-300'
                                                }`}
                                        >
                                            <type.icon className={`w-5 h-5 ${type.color}`} />
                                            <span className="font-medium">{type.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* URL */}
                            <div>
                                <label className="label">URL *</label>
                                <input
                                    type="url"
                                    value={formData.url}
                                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                                    placeholder="https://youtube.com/watch?v=..."
                                    className="input"
                                    required
                                />
                            </div>

                            {/* Title */}
                            <div>
                                <label className="label">Título *</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="Nome do conteúdo"
                                    className="input"
                                    required
                                />
                            </div>

                            {/* Description */}
                            <div>
                                <label className="label">Descrição</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Breve descrição..."
                                    className="input"
                                    rows={2}
                                />
                            </div>

                            {/* Category */}
                            <div>
                                <label className="label">Categoria *</label>
                                <select
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                    className="input"
                                    required
                                >
                                    <option value="">Selecione...</option>
                                    {CATEGORIES.map((cat) => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Featured */}
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.is_featured}
                                    onChange={(e) =>
                                        setFormData({ ...formData, is_featured: e.target.checked })
                                    }
                                    className="rounded border-gray-300 text-primary-600"
                                />
                                <span className="text-sm font-medium text-gray-700">
                                    Marcar como destaque
                                </span>
                            </label>

                            {/* Actions */}
                            <div className="flex gap-3 pt-4">
                                <button type="button" onClick={closeModal} className="btn-secondary flex-1">
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={addMutation.isPending}
                                    className="btn-primary flex-1"
                                >
                                    {addMutation.isPending ? (
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    ) : (
                                        <Plus className="w-4 h-4 mr-2" />
                                    )}
                                    Adicionar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

// Content Card Component
function ContentCard({
    content,
    onDelete,
    onToggleFeatured,
}: {
    content: CuratedContent;
    onDelete: () => void;
    onToggleFeatured: () => void;
}) {
    const [showMenu, setShowMenu] = useState(false);

    const typeConfig = CONTENT_TYPES.find((t) => t.value === content.type);
    const TypeIcon = typeConfig?.icon || Video;

    return (
        <div className="card-hover overflow-hidden">
            {/* Thumbnail */}
            <div className="relative aspect-video bg-gray-100 -mx-6 -mt-6 mb-4">
                {content.thumbnail_url ? (
                    <img
                        src={content.thumbnail_url}
                        alt={content.title}
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <TypeIcon className={`w-12 h-12 ${typeConfig?.color || 'text-gray-400'}`} />
                    </div>
                )}

                {/* Featured badge */}
                {content.is_featured && (
                    <div className="absolute top-2 left-2 badge bg-yellow-500 text-white flex items-center gap-1">
                        <Star className="w-3 h-3" /> Destaque
                    </div>
                )}

                {/* Duration */}
                {content.duration_seconds && (
                    <div className="absolute bottom-2 right-2 badge bg-black/70 text-white text-xs">
                        {Math.floor(content.duration_seconds / 60)}:{(content.duration_seconds % 60).toString().padStart(2, '0')}
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="space-y-2">
                <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-gray-900 line-clamp-2">{content.title}</h3>
                    <div className="relative">
                        <button
                            onClick={() => setShowMenu(!showMenu)}
                            className="p-1 hover:bg-gray-100 rounded"
                        >
                            <MoreVertical className="w-4 h-4" />
                        </button>

                        {showMenu && (
                            <>
                                <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                                <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border py-1 z-20 min-w-[140px]">
                                    <button
                                        onClick={() => {
                                            onToggleFeatured();
                                            setShowMenu(false);
                                        }}
                                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                    >
                                        <Star className="w-4 h-4" />
                                        {content.is_featured ? 'Remover Destaque' : 'Destacar'}
                                    </button>
                                    <a
                                        href={content.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                        Abrir
                                    </a>
                                    <button
                                        onClick={() => {
                                            if (confirm('Excluir este conteúdo?')) {
                                                onDelete();
                                            }
                                            setShowMenu(false);
                                        }}
                                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 text-red-600 flex items-center gap-2"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Excluir
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {content.description && (
                    <p className="text-sm text-gray-500 line-clamp-2">{content.description}</p>
                )}

                <div className="flex items-center gap-2 flex-wrap">
                    <span className={`badge badge-secondary flex items-center gap-1`}>
                        <TypeIcon className={`w-3 h-3 ${typeConfig?.color}`} />
                        {typeConfig?.label}
                    </span>
                    <span className="badge badge-secondary">{content.category}</span>
                </div>
            </div>
        </div>
    );
}
